import Accordion, { AccordionItem, AccordionProps } from '@/components/_atoms/Accordion';
import Heading from '@/components/_atoms/Heading';
import { marginX, mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';

export interface MediaAccordionItem extends AccordionItem {
  media: ReactNode;
}

export interface MediaAccordionProps extends AccordionProps {
  heading?: string;
  description?: ReactNode;
  items: MediaAccordionItem[];
}

const MediaAccordion: FC<PropsWithClassName & MediaAccordionProps> = (props) => {
  const [activeIndex, setActiveIndex] = useState<number>(props.activeIndex!);

  return (
    <div className={props.className}>
      <Grid>
        <HeadingWrapper>
          {!!props.heading ? <Heading level={2}>{props.heading}</Heading> : null}
          {!!props.description ? <div>{props.description}</div> : null}
        </HeadingWrapper>

        <MediaWrapper key={activeIndex}>{props.items[activeIndex].media}</MediaWrapper>

        <AccordionWrapper>
          <Accordion
            items={props.items}
            activeIndex={activeIndex}
            onChangeActiveIndex={(index) => {
              setActiveIndex(index);
            }}
          />
        </AccordionWrapper>
      </Grid>
    </div>
  );
};

MediaAccordion.defaultProps = {
  items: [],
  activeIndex: 0,
};

export default MediaAccordion;

// Styled Components

const Grid = styled.div`
  ${mediaBreakpointUp('desktop')} {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: ${remCalc(20)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: repeat(18, 1fr);
  }
`;

const MediaWrapper = styled.div`
  margin-bottom: ${remCalc(40)};
  border-radius: 10px;
  overflow: hidden;

  img,
  video {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 7 / -1;
    align-self: flex-start;
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: 0;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 10 / -1;
  }
`;

const AccordionWrapper = styled.div`
  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / 6;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 1 / 9;
  }
`;

const HeadingWrapper = styled.div`
  grid-row: 1;
  text-align: center;
  max-width: ${remCalc(928)};
  ${marginX('auto')}
  margin-bottom: 30px;

  display: flex;
  flex-direction: column;
  gap: ${remCalc(30)};

  div {
    ${customTextSize(16, 24)};

    a {
      text-decoration: underline;
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: 60px;
    grid-column: 3 / -3;
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: 80px;

    div {
      ${customTextSize(24, 34)};
    }
  }
`;
